import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import { companyConfigService, type MarketplaceIntegrationConfig } from '../services/companyConfigService';
import { mercadoPagoOAuthService } from '../services/mercadoPagoOAuthService';
import { authService } from '../services';

const MercadoPagoIntegrationPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [marketplace, setMarketplace] = useState<MarketplaceIntegrationConfig | null>(null);
  const [applicationFeePercent, setApplicationFeePercent] = useState<string>('');
  const [fixedFeeAmount, setFixedFeeAmount] = useState<string>('');
  const [savingFees, setSavingFees] = useState(false);

  useEffect(() => {
    loadMarketplaceConfig();
  }, []);

  const loadMarketplaceConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const config = await companyConfigService.getMyConfig();
      const marketplaceConfig = config?.modules?.payments?.marketplace;
      setMarketplace(marketplaceConfig || { status: 'NOT_STARTED' });
      setApplicationFeePercent(String(marketplaceConfig?.applicationFeePercent ?? ''));
      setFixedFeeAmount(String(marketplaceConfig?.fixedFeeAmount ?? ''));
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar configuração do marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleStartOAuth = async () => {
    const activeCompanyCnpj = authService.getActiveCompanyCnpj();
    if (!activeCompanyCnpj) {
      setError('CNPJ não encontrado para iniciar integração');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const currentUrl = window.location.origin;
      const result = await mercadoPagoOAuthService.initiateOAuth(activeCompanyCnpj, currentUrl);
      window.location.href = result.authorizationUrl;
    } catch (err: any) {
      setError(err.message || 'Erro ao iniciar integração com Mercado Pago');
      setLoading(false);
    }
  };

  const handleRefreshToken = async () => {
    const activeCompanyCnpj = authService.getActiveCompanyCnpj();
    if (!activeCompanyCnpj) {
      setError('CNPJ não encontrado para atualizar credenciais');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await mercadoPagoOAuthService.refreshToken(activeCompanyCnpj);
      setSuccess('Credenciais atualizadas com sucesso');
      await loadMarketplaceConfig();
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar credenciais');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFees = async () => {
    try {
      setSavingFees(true);
      setError(null);
      setSuccess(null);

      const feePercent = applicationFeePercent.trim() ? parseFloat(applicationFeePercent) : undefined;
      const feeFixed = fixedFeeAmount.trim() ? parseFloat(fixedFeeAmount) : undefined;

      if (feePercent !== undefined && (isNaN(feePercent) || feePercent < 0 || feePercent > 100)) {
        setError('Taxa percentual deve estar entre 0 e 100');
        return;
      }

      if (feeFixed !== undefined && (isNaN(feeFixed) || feeFixed < 0)) {
        setError('Taxa fixa deve ser um valor positivo');
        return;
      }

      const config = await companyConfigService.getMyConfig();
      const updatedMarketplace: MarketplaceIntegrationConfig = {
        ...(marketplace || { status: 'NOT_STARTED' }),
        applicationFeePercent: feePercent,
        fixedFeeAmount: feeFixed,
      };

      await companyConfigService.updateMyConfig({
        modules: {
          ...config?.modules,
          payments: {
            ...config?.modules?.payments,
            enabled: config?.modules?.payments?.enabled ?? false,
            marketplace: updatedMarketplace,
          },
        },
      });

      setSuccess('Taxas atualizadas com sucesso');
      await loadMarketplaceConfig();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar taxas');
    } finally {
      setSavingFees(false);
    }
  };

  const getStatusChip = () => {
    if (!marketplace) return null;

    const statusConfig = {
      NOT_STARTED: { label: 'Não Iniciado', color: 'default' as const, icon: <PendingIcon /> },
      PENDING: { label: 'Pendente', color: 'warning' as const, icon: <PendingIcon /> },
      AUTHORIZED: { label: 'Autorizado', color: 'success' as const, icon: <CheckCircleIcon /> },
      ERROR: { label: 'Erro', color: 'error' as const, icon: <ErrorIcon /> },
      EXPIRED: { label: 'Expirado', color: 'error' as const, icon: <ErrorIcon /> },
    };

    const config = statusConfig[marketplace.status];
    return <Chip label={config.label} color={config.color} icon={config.icon} />;
  };

  const formatDate = (isoDate?: string) => {
    if (!isoDate) return '-';
    return new Date(isoDate).toLocaleString('pt-BR');
  };

  if (loading && !marketplace) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Integração Mercado Pago Marketplace
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure a integração com o Mercado Pago para receber pagamentos com split automático de taxas.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Status da Integração
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            {getStatusChip()}
            {marketplace?.status === 'AUTHORIZED' && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshToken}
                disabled={loading}
              >
                Atualizar Credenciais
              </Button>
            )}
          </Stack>

          {marketplace?.lastError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Último erro:</strong> {marketplace.lastError}
              </Typography>
            </Alert>
          )}

          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              <strong>Última tentativa:</strong> {formatDate(marketplace?.lastAttemptAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Vinculado em:</strong> {formatDate(marketplace?.linkedAt)}
            </Typography>
            {marketplace?.sellerNickname && (
              <Typography variant="body2" color="text.secondary">
                <strong>Vendedor:</strong> {marketplace.sellerNickname}
              </Typography>
            )}
            {marketplace?.sellerEmail && (
              <Typography variant="body2" color="text.secondary">
                <strong>Email:</strong> {marketplace.sellerEmail}
              </Typography>
            )}
            {marketplace?.collectorId && (
              <Typography variant="body2" color="text.secondary">
                <strong>Collector ID:</strong> {marketplace.collectorId}
              </Typography>
            )}
            {marketplace?.credentialMp?.expiresAt && (
              <Typography variant="body2" color="text.secondary">
                <strong>Token expira em:</strong> {formatDate(marketplace.credentialMp.expiresAt)}
              </Typography>
            )}
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Configuração de Taxas
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure as taxas que serão retidas pela plataforma em cada transação.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Taxa Percentual (%)"
              type="number"
              value={applicationFeePercent}
              onChange={(e) => setApplicationFeePercent(e.target.value)}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              helperText="Percentual sobre o valor da transação (ex: 5 para 5%)"
            />
            <TextField
              fullWidth
              label="Taxa Fixa (R$)"
              type="number"
              value={fixedFeeAmount}
              onChange={(e) => setFixedFeeAmount(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Valor fixo por transação (ex: 2.50)"
            />
          </Stack>

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveFees}
              disabled={savingFees}
              startIcon={savingFees ? <CircularProgress size={20} /> : null}
            >
              {savingFees ? 'Salvando...' : 'Salvar Taxas'}
            </Button>
          </Box>

          {(marketplace?.applicationFeePercent || marketplace?.fixedFeeAmount) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Exemplo de cálculo:</strong> Em uma transação de R$ 100,00:
                <br />
                {marketplace.applicationFeePercent && (
                  <>Taxa percentual: R$ {((100 * marketplace.applicationFeePercent) / 100).toFixed(2)}<br /></>
                )}
                {marketplace.fixedFeeAmount && (
                  <>Taxa fixa: R$ {marketplace.fixedFeeAmount.toFixed(2)}<br /></>
                )}
                <strong>Total retido pela plataforma:</strong> R${' '}
                {(
                  ((marketplace.applicationFeePercent || 0) * 100) / 100 +
                  (marketplace.fixedFeeAmount || 0)
                ).toFixed(2)}
                <br />
                <strong>Valor líquido para o vendedor:</strong> R${' '}
                {(
                  100 -
                  ((marketplace.applicationFeePercent || 0) * 100) / 100 -
                  (marketplace.fixedFeeAmount || 0)
                ).toFixed(2)}
              </Typography>
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="h6" gutterBottom>
            Ações
          </Typography>
          {marketplace?.status === 'NOT_STARTED' || 
           marketplace?.status === 'ERROR' || 
           marketplace?.status === 'EXPIRED' ||
           (marketplace?.status === 'PENDING' && !marketplace?.collectorId) ? (
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<LinkIcon />}
              onClick={handleStartOAuth}
              disabled={loading}
            >
              {loading ? 'Iniciando...' : 'Conectar com Mercado Pago'}
            </Button>
          ) : (
            <Alert severity="info">
              <Typography variant="body2">
                Integração já configurada. Use o botão "Atualizar Credenciais" acima se necessário.
              </Typography>
            </Alert>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default MercadoPagoIntegrationPage;
